import { useEffect } from 'react';
import styled from 'styled-components';
import { useLazyTraceEconFactQuery } from '@shared/api';
import type { EconCalcFact, EconExtractedRow, EconSource } from '@shared/api';
import { Loader } from '@shared/ui/Progress';
import { fmtNumber, labelDocType, labelMetric, labelRowKind } from '@entities/economic/types';
import { drawLine, flowIn } from './econAnimations';

/**
 * Интерактивная трассировка расчётного факта: факт → исходные строки →
 * документы-источники. Цепочка рисуется как горизонтальный flow из трёх
 * колонок, соединённых SVG-линиями, которые «прорисовываются» слева направо
 * (stroke-dashoffset). Узлы появляются со staggered-задержкой.
 *
 * Данные тянем лениво по record_uid: панель раскрывается только по клику на
 * факт, незачем грузить трассу для всех фактов сразу.
 */

const Panel = styled.div`
  margin-top: ${({ theme }) => theme.spacing(3)};
  padding: ${({ theme }) => theme.spacing(4)};
  border: 1px dashed ${({ theme }) => theme.colors.borderStrong};
  border-radius: ${({ theme }) => theme.radius.lg};
  background: ${({ theme }) => theme.colors.surface};
  overflow-x: auto;
`;

const Flow = styled.div`
  display: grid;
  grid-template-columns: 1fr auto 1.2fr auto 1fr;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(2)};
  min-width: 720px;

  @media (max-width: 760px) {
    /* На узких экранах SVG-коннекторы прячем, колонки складываем вертикально —
       горизонтальный flow на 360px нечитаем. */
    grid-template-columns: 1fr;
    min-width: 0;
    gap: ${({ theme }) => theme.spacing(3)};
  }
`;

const Column = styled.div<{ $index: number }>`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(2)};
  animation: ${flowIn} 360ms ${({ theme }) => theme.motion.easeOut} both;
  animation-delay: ${({ $index }) => `${$index * 120}ms`};
`;

const ColLabel = styled.div`
  font-size: 10px;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.text.muted};
`;

const Node = styled.div<{ $accent?: boolean }>`
  border: 1px solid
    ${({ theme, $accent }) => ($accent ? theme.colors.primary : theme.colors.border)};
  background: ${({ theme, $accent }) =>
    $accent ? theme.colors.primarySoft : theme.colors.backgroundElevated};
  border-radius: ${({ theme }) => theme.radius.md};
  padding: ${({ theme }) => `${theme.spacing(2.5)} ${theme.spacing(3)}`};
  box-shadow: ${({ theme }) => theme.shadow.xs};
`;

const NodeTitle = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.primary};
`;

const NodeMeta = styled.div`
  font-size: 11px;
  color: ${({ theme }) => theme.colors.text.secondary};
  margin-top: ${({ theme }) => theme.spacing(0.5)};
  font-variant-numeric: tabular-nums;
`;

const Uid = styled.code`
  font-family: ${({ theme }) => theme.font.mono};
  font-size: 10.5px;
  color: ${({ theme }) => theme.colors.text.muted};
`;

const Connector = styled.svg`
  width: 56px;
  height: 28px;
  display: block;

  & path {
    fill: none;
    stroke: ${({ theme }) => theme.colors.primary};
    stroke-width: 1.75;
    --dash-len: 64;
    stroke-dasharray: 64;
    animation: ${drawLine} 520ms ${({ theme }) => theme.motion.easeOut} both;
  }

  @media (max-width: 760px) {
    display: none;
  }
`;

const Err = styled.div`
  font-size: 13px;
  color: ${({ theme }) => theme.colors.status.danger};
`;

const LoadingRow = styled.div`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(2)};
  font-size: 13px;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

function LineConnector({ delay }: { delay: number }) {
  // S-кривая между колонками; прорисовка стартует после появления левого узла.
  return (
    <Connector viewBox="0 0 56 28" aria-hidden="true">
      <path d="M0 14 C 20 14, 36 14, 56 14" style={{ animationDelay: `${delay}ms` }} />
    </Connector>
  );
}

function rowKindLabel(row: EconExtractedRow): string {
  return row.kind ? labelRowKind(row.kind) : 'строка';
}

function sourceLabel(src: EconSource): string {
  return src.filename || labelDocType(src.doc_type) || 'документ';
}

/** Сокращённый технический код записи для показа не-айтишнику: первые 8 знаков
 *  UUID + многоточие (полный — в title при наведении). Длинный UUID в интерфейсе
 *  читается как шум (ТЗ п.5.6 — без айтишных терминов). */
function shortUid(uid: string): string {
  return uid.length > 10 ? `${uid.slice(0, 8)}…` : uid;
}

interface Props {
  recordUid: string;
  /** Денормализованный факт из карточки — показываем сразу, пока грузится
   *  трасса; ответ trace его всё равно вернёт, но мгновенный заголовок лучше. */
  fact: EconCalcFact;
}

export function TraceView({ recordUid, fact }: Props) {
  const [trigger, { data, isFetching, isError }] = useLazyTraceEconFactQuery();

  useEffect(() => {
    trigger(recordUid);
  }, [recordUid, trigger]);

  const rows = data?.rows ?? [];
  const sources = data?.sources ?? [];

  return (
    <Panel>
      {isFetching && !data && (
        <LoadingRow>
          <Loader />
          Строим цепочку трассировки…
        </LoadingRow>
      )}
      {isError && <Err>Не удалось загрузить трассировку факта.</Err>}
      {data && (
        <Flow>
          <Column $index={0}>
            <ColLabel>Расчётный факт</ColLabel>
            <Node $accent>
              <NodeTitle>{labelMetric(data.fact.metric || fact.metric)}</NodeTitle>
              <NodeMeta>
                план {fmtNumber(data.fact.value_plan)} · факт {fmtNumber(data.fact.value_fact)}
              </NodeMeta>
              <NodeMeta>
                код <Uid title={data.fact.record_uid}>{shortUid(data.fact.record_uid)}</Uid>
              </NodeMeta>
            </Node>
          </Column>

          <LineConnector delay={120} />

          <Column $index={1}>
            <ColLabel>Исходные строки · {rows.length}</ColLabel>
            {rows.length === 0 && <NodeMeta>Строки не привязаны</NodeMeta>}
            {rows.map((row) => (
              <Node key={row.id}>
                <NodeTitle>{rowKindLabel(row)}</NodeTitle>
                <NodeMeta>{row.source_ref ?? '—'}</NodeMeta>
                <NodeMeta>
                  код <Uid title={row.record_uid}>{shortUid(row.record_uid)}</Uid>
                </NodeMeta>
              </Node>
            ))}
          </Column>

          <LineConnector delay={240} />

          <Column $index={2}>
            <ColLabel>Документы · {sources.length}</ColLabel>
            {sources.length === 0 && <NodeMeta>Источники не найдены</NodeMeta>}
            {sources.map((src) => (
              <Node key={src.id}>
                <NodeTitle>{sourceLabel(src)}</NodeTitle>
                <NodeMeta>{labelDocType(src.doc_type)}</NodeMeta>
              </Node>
            ))}
          </Column>
        </Flow>
      )}
    </Panel>
  );
}
